import { blue, gold, green, grey, orange, red, textMuted } from '@/theme/colors'
import { GetAdminOverviewResponse } from '@orbit/api-types'
import React from 'react'
import {
	Chart,
	ChartCard,
	ChartTitle,
	Legend,
	LegendItem,
	Swatch,
} from './TrendChart.styles'
import { EmptyNoteInline } from '@components/EmptyState'

type TrendPoint = GetAdminOverviewResponse['trend'][number]

const SERIES: { key: keyof Omit<TrendPoint, 'date'>; label: string; color: string }[] = [
	{ key: 'users', label: 'Users', color: blue },
	{ key: 'trips', label: 'Trips', color: green },
	{ key: 'events', label: 'Events', color: gold },
	{ key: 'ideas', label: 'Ideas', color: orange },
	{ key: 'expenses', label: 'Expenses', color: red },
]

const VIEW_W = 720
const VIEW_H = 240
const PAD = { top: 16, right: 16, bottom: 28, left: 36 }
const PLOT_W = VIEW_W - PAD.left - PAD.right
const PLOT_H = VIEW_H - PAD.top - PAD.bottom

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `YYYYMMDD` → short axis label like `Jun 3`. */
const formatDay = (yyyymmdd: string): string => {
	const month = Number(yyyymmdd.slice(4, 6)) - 1
	const day = Number(yyyymmdd.slice(6, 8))
	return `${MONTHS[month] ?? '?'} ${day}`
}

/**
 * Dependency-free multi-series line chart for the daily overview trend. Each
 * series is a polyline scaled to the largest value across all series, so the
 * three lines share one y-axis.
 */
const TrendChart: React.FC<{ trend: TrendPoint[] }> = ({ trend }) => {
	if (trend.length === 0) {
		return (
			<ChartCard>
				<ChartTitle>Daily trend</ChartTitle>
				<EmptyNoteInline>No activity in the selected range.</EmptyNoteInline>
			</ChartCard>
		)
	}

	const max = Math.max(1, ...trend.flatMap(p => SERIES.map(s => p[s.key])))
	const x = (i: number): number =>
		trend.length === 1 ? PAD.left + PLOT_W / 2 : PAD.left + (i / (trend.length - 1)) * PLOT_W
	const y = (value: number): number => PAD.top + PLOT_H - (value / max) * PLOT_H

	return (
		<ChartCard>
			<ChartTitle>Daily trend</ChartTitle>
			<Legend>
				{SERIES.map(s => (
					<LegendItem key={s.key}>
						<Swatch $color={s.color} />
						{s.label}
					</LegendItem>
				))}
			</Legend>
			<Chart viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label="Daily activity trend">
				{/* axes */}
				<line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + PLOT_H} stroke={grey} />
				<line
					x1={PAD.left}
					y1={PAD.top + PLOT_H}
					x2={PAD.left + PLOT_W}
					y2={PAD.top + PLOT_H}
					stroke={grey}
				/>
				{/* y-axis bounds */}
				<text x={PAD.left - 6} y={PAD.top + 4} textAnchor="end" fontSize="11" fill={textMuted}>
					{max}
				</text>
				<text x={PAD.left - 6} y={PAD.top + PLOT_H} textAnchor="end" fontSize="11" fill={textMuted}>
					0
				</text>
				{/* x-axis first/last day */}
				<text x={x(0)} y={VIEW_H - 8} textAnchor="start" fontSize="11" fill={textMuted}>
					{formatDay(trend[0]!.date)}
				</text>
				{trend.length > 1 && (
					<text x={x(trend.length - 1)} y={VIEW_H - 8} textAnchor="end" fontSize="11" fill={textMuted}>
						{formatDay(trend[trend.length - 1]!.date)}
					</text>
				)}
				{/* series */}
				{SERIES.map(s => (
					<polyline
						key={s.key}
						fill="none"
						stroke={s.color}
						strokeWidth={2}
						strokeLinejoin="round"
						points={trend.map((p, i) => `${x(i)},${y(p[s.key])}`).join(' ')}
					/>
				))}
			</Chart>
		</ChartCard>
	)
}

export default TrendChart
